import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Icon, { IconName } from '../components/Icon';
import { useTranslation } from '../i18n';
import './Toast.css';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  variant?: ToastVariant;
  /** Up to two actions, e.g. "Undo" and "Add note" */
  actions?: ToastAction[];
  /** Milliseconds; 0 keeps the toast until dismissed */
  duration?: number;
}

interface ToastItem extends Required<Omit<ToastOptions, 'actions'>> {
  id: number;
  message: string;
  actions: ToastAction[];
}

interface ToastContextValue {
  show: (message: string, options?: ToastOptions) => number;
  success: (message: string, options?: Omit<ToastOptions, 'variant'>) => number;
  error: (message: string, options?: Omit<ToastOptions, 'variant'>) => number;
  info: (message: string, options?: Omit<ToastOptions, 'variant'>) => number;
  warning: (message: string, options?: Omit<ToastOptions, 'variant'>) => number;
  dismiss: (id: number) => void;
}

const DEFAULT_DURATION: Record<ToastVariant, number> = {
  success: 4000,
  info: 4000,
  warning: 6000,
  error: 6000,
};

/** Toasts with an action stay long enough to reach the button */
const ACTION_MIN_DURATION = 5000;
const MAX_VISIBLE = 3;

const VARIANT_ICON: Record<ToastVariant, IconName> = {
  success: 'circleCheck',
  error: 'warning',
  warning: 'warning',
  info: 'info',
};

const ToastContext = createContext<ToastContextValue | null>(null);

const ToastView: React.FC<{ toast: ToastItem; onDismiss: (id: number) => void }> = ({ toast, onDismiss }) => {
  const { t } = useTranslation();
  const timerRef = useRef<number | null>(null);
  const remainingRef = useRef(toast.duration);
  const startedAtRef = useRef(0);

  const clear = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const start = useCallback(() => {
    if (toast.duration === 0) return;
    clear();
    startedAtRef.current = Date.now();
    timerRef.current = window.setTimeout(() => onDismiss(toast.id), remainingRef.current);
  }, [toast.duration, toast.id, onDismiss]);

  const pause = () => {
    if (timerRef.current === null) return;
    clear();
    remainingRef.current = Math.max(1000, remainingRef.current - (Date.now() - startedAtRef.current));
  };

  useEffect(() => {
    start();
    return clear;
  }, [start]);

  return (
    <div
      className={`toast toast--${toast.variant}`}
      role={toast.variant === 'error' ? 'alert' : 'status'}
      onMouseEnter={pause}
      onMouseLeave={start}
      onFocus={pause}
      onBlur={start}
    >
      <Icon name={VARIANT_ICON[toast.variant]} className="toast__icon" />
      <span className="toast__message">{toast.message}</span>
      {toast.actions.map((action) => (
        <button
          key={action.label}
          type="button"
          className="toast__action"
          onClick={() => {
            action.onClick();
            onDismiss(toast.id);
          }}
        >
          {action.label}
        </button>
      ))}
      <button
        type="button"
        className="toast__close"
        onClick={() => onDismiss(toast.id)}
        aria-label={t('common.close')}
      >
        <Icon name="close" />
      </button>
    </div>
  );
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback((message: string, options: ToastOptions = {}) => {
    const variant = options.variant ?? 'info';
    const actions = (options.actions ?? []).slice(0, 2);
    let duration = options.duration ?? DEFAULT_DURATION[variant];
    if (actions.length > 0 && duration !== 0) {
      duration = Math.max(duration, ACTION_MIN_DURATION);
    }
    const id = ++idRef.current;
    setToasts((prev) => [...prev, { id, message, variant, actions, duration }].slice(-MAX_VISIBLE));
    return id;
  }, []);

  const value = useMemo<ToastContextValue>(() => ({
    show,
    dismiss,
    success: (message, options) => show(message, { ...options, variant: 'success' }),
    error: (message, options) => show(message, { ...options, variant: 'error' }),
    info: (message, options) => show(message, { ...options, variant: 'info' }),
    warning: (message, options) => show(message, { ...options, variant: 'warning' }),
  }), [show, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-region" aria-live="polite" aria-relevant="additions">
        {toasts.map((toast) => (
          <ToastView key={toast.id} toast={toast} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
